/* eslint no-console: ["error", { allow: ["warn", "error"] }] */
import {
    Besida,
    BESIDA_ROLE_BUYER,
    BESIDA_ROLE_COMPANY,
} from '@evo/chat-core';

import { Config } from './../core/config';
import { getParamByName } from './../libs/utils';
import * as c from './constants';
import * as a from './actions';

function besidaHandlerFxFactory(stream, actionName) {
    return (requestMeta, data) => stream.send(actionName, { requestMeta, data });
}

export function connectBesidaFx(userRole, chatsStatus, userHasCompany, userHasC2CCompany, knockAfterConnection) {
    const { IS_DEBUG, getSocketConnectionObj, addStateCallback, KOMORA_URL } = Config.data.appState;

    return async (stream) => {
        const socketConnectionObj = getSocketConnectionObj();
        const isSocketNotInited = socketConnectionObj.state() === c.NOT_INITIALIZED_SOCKET_STATE;

        if (isSocketNotInited) {
            addStateCallback((payload) => {
                if (payload.status === c.BESIDA_ACTIVE_CONNECTION) {
                    return stream.send(a.EVENT_INIT)
                }
                return void 0;
            });
            return stream.send(a.EVENT_BESIDA_INIT_FAIL)
        }

        const besida = new Besida(socketConnectionObj, userRole, KOMORA_URL, IS_DEBUG);

        // dispatcher back bunch
        besida.registerHandler('message', besidaHandlerFxFactory(stream, a.EVENT_BESIDA_MESSAGE));
        besida.registerHandler('counters', besidaHandlerFxFactory(stream, a.EVENT_BESIDA_COUNTERS));
        besida.registerHandler('registry', besidaHandlerFxFactory(stream, a.EVENT_BESIDA_REGISTRY));
        besida.registerHandler('ban', besidaHandlerFxFactory(stream, a.EVENT_BESIDA_BAN));
        besida.registerHandler('typing', besidaHandlerFxFactory(stream, a.EVENT_BESIDA_TYPING));
        besida.registerHandler('mark_read', besidaHandlerFxFactory(stream, a.EVENT_BESIDA_MARK_READ));
        besida.registerHandler('room', besidaHandlerFxFactory(stream, a.EVENT_BESIDA_ROOM));
        besida.registerHandler('error', besidaHandlerFxFactory(stream, a.EVENT_BESIDA_ERROR));

        (socketConnectionObj.state() !== c.BESIDA_ACTIVE_CONNECTION) && await besida.connect();

        stream.send(a.EVENT_BESIDA_CONNECTED, besida);

        await besida.chatList(chatsStatus, BESIDA_ROLE_BUYER);
        (userHasCompany || userHasC2CCompany) && await besida.chatList(chatsStatus, BESIDA_ROLE_COMPANY);
        stream.send(a.EVENT_BESIDA_INITIALIZED);
        if (knockAfterConnection) return stream.send(a.EVENT_BESIDA_KNOCK_KNOCK, { besida });
        if (getParamByName(c.CHAT_AUTO_OPEN_MARK)) return stream.send(a.EVENT_OPEN_DIALOG);
        return stream.send(a.EVENT_AUTO_OPEN_DIALOG, { besida });
    };
}

export function besidaJoinFx(besida, roomIdent) {
    return (stream) => besida.join(roomIdent)
        .then(() => stream.send(a.EVENT_OPEN_ROOM_SUCCESS));
}

export function besidaSendMsgFx(besida, roomIdent, body, platform, source, device) {
    return () => besida.sendMessage(roomIdent, body, platform, source, device);
}
export function besidaRetrySendMsgFx(besida, roomIdent, msgId) {
    return () => besida.retrySendMessage(roomIdent, msgId);
}

export function besidaRetrySendFileFx(besida, roomIdent, msgId) {
    return () => besida.retrySendFile(roomIdent, msgId);
}

export function besidaRetrySendFileCtxFx(besida, roomIdent, msgId) {
    return () => besida.retrySendFileCtx(roomIdent, msgId);
}

export function besidaSendCtxFx(
    besida, roomIdent, contextItemId, contextItemType, contextItemPrice,
    contextItemCurrency, contextSource, contextDescription, contextMeta,
    platform, source, device
) {
    return (stream) => besida.sendContext(
        roomIdent, Number(contextItemId), contextItemType, contextItemPrice,
        contextItemCurrency, contextSource, contextDescription, contextMeta,
        platform, source, device)
        .then(() => stream.send(a.EVENT_BESIDA_SEND_CONTEXT_SUCCESS));
}

export function besidaSendFilesCtxFx(besida, roomIdent, contextSource, platform,
    source, device, files,
) {
    return (stream) => besida.sendFilesContext(roomIdent, contextSource, platform,
        source, device, files,
    ).then(() => stream.send(a.EVENT_BESIDA_SEND_CONTEXT_SUCCESS));
}

export function besidaKnockKnockFx(besida, theme) {
    return (stream) => besida.knockKnock(theme)
        .then((data) => {
            stream.send(a.EVENT_OPEN_ROOM, data && data.get('room_ident'));
        });
}

export function besidaMarkMsgReadFx(besida, roomIdent, msgId) {
    return (stream) => {
        // Detect if browser tab is active or user has switched away
        if (document.hidden) {
            return void 0;
        }
        return besida.markMsgRead(roomIdent, msgId)
        .then(() => stream.send(a.EVENT_BESIDA_MARK_MSG_READ_SUCCESS))
    };
}

export function besidaChatMetaFx(besida, roomIdent) {
    return (stream) => besida.sendChatMeta(roomIdent)
        .then(() => stream.send(a.EVENT_BESIDA_CHAT_META_SUCCESS, roomIdent));
}

export function besidaChatHistoryFx(besida, roomIdent) {
    return (stream) => besida.lastMsgFromHistory(roomIdent)
        .then(() => stream.send(a.EVENT_BESIDA_CHAT_HISTORY_SUCCESS, roomIdent));
}

export function besidaChatListLoadFx(besida, chatsStatus, userRole,) {
    return (stream) => besida.chatList(chatsStatus, userRole)
        .then(() => stream.send(a.CHAT_LIST_LOAD_SUCCESS));
}

export function besidaDialogFetchHistoryFx(besida, roomIdent, lastMsgId) {
    return (stream) => besida.lazyHistory(roomIdent, lastMsgId)
        .then((history) => stream.send(a.EVENT_DIALOG_FETCH_HISTORY_SUCCESS, { roomIdent, history }));
}

export function besidaSendBanListFx(besida, roomIdent) {
    return (stream) => besida.sendBanList(roomIdent)
        .then((response) => {
            stream.send(a.EVENT_BESIDA_SEND_BAN_LIST_SUCCESS, response);
        });
}

export function besidaSendUnbanListFx(besida, roomIdent) {
    return (stream) => besida.sendUnbanList(roomIdent)
        .then((response) => {
            stream.send(a.EVENT_BESIDA_SEND_UNBAN_LIST_SUCCESS, response);
        });
}

export function besidaSendTypingFx(besida, roomIdent) {
    return () => besida.sendTyping(roomIdent);
}

export function openRoomFx(roomIdent) {
    return (stream) => stream.send(a.EVENT_OPEN_ROOM, roomIdent);
}
